import dayjs from "dayjs";
import type { Point, PointOptionsObject, TooltipOptions } from "highcharts";
import { renderToString } from "react-dom/server";

import { tooltipContainerConfig, shortMonthAndYear } from "@similarweb/pro-logic-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { changeFilter } from "app/filters/ngFilters";

import type { MetricKey, TimeGranularity } from "../../types";
import { METRIC_CONFIGURATION } from "../config";

import { formatTooltipTitle } from "./dateFormatters";

type PeriodOverPeriodPoint = Point & {
    options: PointOptionsObject & {
        originalPreviousDate?: number;
        change?: number;
    };
};

export const getPopTooltipConfig = (metric: MetricKey, granularity: TimeGranularity): TooltipOptions => {
    return {
        ...tooltipContainerConfig().tooltip,
        shared: true,
        outside: false,
        useHTML: true,
        formatter: function () {
            const formatter = METRIC_CONFIGURATION[metric].tooltipFormatter;
            const title = formatTooltipTitle(this.x, granularity, true);

            const currentPoints = this.points.filter((p) =>
                p.series.userOptions.id.includes("-current"),
            ) as Array<PeriodOverPeriodPoint>;
            const previousPoints = this.points.filter((p) =>
                p.series.userOptions.id.includes("-previous"),
            ) as Array<PeriodOverPeriodPoint>;

            const currentTimestamp = dayjs.utc(this.x).format(shortMonthAndYear);
            const previousTimestamp = currentPoints[0]?.options.originalPreviousDate
                ? dayjs.utc(currentPoints[0].options.originalPreviousDate).format(shortMonthAndYear)
                : "Previous";

            const data = currentPoints.map((currentPoint) => {
                const previousPoint = previousPoints.find((p) => p.series.name === currentPoint.series.name);
                return {
                    category: {
                        categoryName: currentPoint.series.name,
                        color: currentPoint.series.color,
                    },
                    currentPeriod: formatter(currentPoint.y),
                    previousPeriod: previousPoint ? formatter(previousPoint.y) : "N/A",
                    change: currentPoint.options.change || 0,
                };
            });

            return renderToString(
                <ChartTooltip
                    width={420}
                    title={title}
                    data={data}
                    columnDefinition={[
                        {
                            align: "left",
                            key: "category",
                            size: "1.5fr",
                            title: translate("common.industry"),
                            selector: "category",
                            render: ({ category }) => (
                                <ChartTooltip.LabelCell
                                    label={category.categoryName}
                                    backgroundColor={category.color as string}
                                />
                            ),
                        },
                        {
                            align: "right",
                            key: "previousPeriod",
                            size: "1fr",
                            title: previousTimestamp,
                            selector: "previousPeriod",
                            render: ({ previousPeriod }) => (
                                <ChartTooltip.ValueCell
                                    data-automation="overview-graph-comparison-tooltip-previous-value"
                                    value={previousPeriod}
                                />
                            ),
                        },
                        {
                            align: "right",
                            key: "currentPeriod",
                            size: "1fr",
                            title: currentTimestamp,
                            selector: "currentPeriod",
                            render: ({ currentPeriod }) => (
                                <ChartTooltip.ValueCell
                                    data-automation="overview-graph-comparison-tooltip-current-value"
                                    value={currentPeriod}
                                />
                            ),
                        },
                        {
                            align: "right",
                            key: "change",
                            size: "0.8fr",
                            title: "Change",
                            selector: "change",
                            render: ({ change }) => (
                                <ChartTooltip.ChangeCell
                                    value={changeFilter()(change, 2)}
                                    isDecrease={change < 0}
                                    unsigned={change === 0}
                                />
                            ),
                        },
                    ]}
                />,
            );
        },
    };
};
